import React from 'react';
import GoogleMapReact from 'google-map-react';
import { useIsMobileScreen } from '../../../hooks/useWindowSize';
import mapContext from '../../../contexts/map-context';
import SearchInThisAreaButton from '../SearchThisAreaButton/SearchInThisAreaButton';
import { generateMapMarkerList } from '../MapMarkerList/generateMapMarkerList';
import { MapContainer } from './Map.styled';
import {
  DEFAULT_CENTER_COORDINATES,
  DEFAULT_ZOOM,
  getMapOptions,
  isGoogleMapsLoaded,
  loadGoogleMaps,
} from './MapUtils';
import {
  useLogger,
  useOnChangeDealerId,
  useOnChangeDealerList,
  useOnChangeHash,
} from './Map.hooks';
import {
  handleGoogleApiLoad,
  handleMapChange,
  handleMapClick,
  handleMapDrag,
  handleMapDragEnd,
} from './Map.handlers';

export default function Map() {
  const {
    marketGoogleKey,
    locale,
    mapZoom,
    mapCenterCoordinates,
    oneHeader,
    selectedDealerId,
    twoClickConsentRequired,
  } = mapContext.useMapState();

  const isMobileScreen = useIsMobileScreen();
  const onMapDragEnd = handleMapDragEnd();
  const onMapDrag = handleMapDrag();
  const onMapChange = handleMapChange();
  const onMapClick = handleMapClick();
  const onGoogleApiLoaded = handleGoogleApiLoad();
  const [isGoogleMapsReady, setIsGoogleMapsReady] = React.useState(isGoogleMapsLoaded);

  useLogger();
  useOnChangeDealerList();
  useOnChangeDealerId();
  useOnChangeHash();

  React.useEffect(() => {
    let isMounted = true;

    void loadGoogleMaps(marketGoogleKey, locale.language)
      .then(() => {
        if (isMounted) {
          setIsGoogleMapsReady(true);
        }
      })
      .catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [marketGoogleKey, locale.language]);

  const googleMapLoader = React.useCallback(
    () => loadGoogleMaps(marketGoogleKey, locale.language).then((googleMaps) => googleMaps.maps),
    [marketGoogleKey, locale.language],
  );
  const googleMapReactLoader = googleMapLoader as unknown as () => undefined;

  const key = isMobileScreen ? mapCenterCoordinates.lat : 'map';
  const mapMarkers = generateMapMarkerList();

  return (
    <MapContainer
      data-test-id="map-component"
      oneHeader={oneHeader}
      hasSelectedDealerId={!!selectedDealerId}
      twoClickConsentRequired={twoClickConsentRequired}
    >
      <>
        <SearchInThisAreaButton />

        {isGoogleMapsReady && (
          <GoogleMapReact
            key={key}
            zoom={mapZoom}
            defaultCenter={DEFAULT_CENTER_COORDINATES}
            defaultZoom={DEFAULT_ZOOM}
            center={mapCenterCoordinates}
            onChange={onMapChange}
            onDrag={onMapDrag}
            onDragEnd={onMapDragEnd}
            onClick={onMapClick}
            onGoogleApiLoaded={onGoogleApiLoaded}
            options={getMapOptions(isMobileScreen)}
            yesIWantToUseGoogleMapApiInternals
            bootstrapURLKeys={undefined}
            googleMapLoader={googleMapReactLoader}
            data-test-id="google-map-react"
          >
            {mapMarkers}
          </GoogleMapReact>
        )}
      </>
    </MapContainer>
  );
}
